import { useQuery } from "@tanstack/react-query";
import { format, isAfter, parseISO, startOfDay } from "date-fns";
import { apiRequest } from "@/lib/queryClient";
import type { ChipKind } from "@/lib/status-chip";

/**
 * A document Sidekick sent that this PA is a party to — an amendment to an
 * existing contract, or a new agreement. Keyed on the document's own parties,
 * not on current assignments, so it stays visible after a reassignment.
 * `counterparty` is the client's name, or "Sidekick" for employment documents.
 */
export type MyContractDoc = {
  id: number;
  title: string;
  summary: string | null;
  contract_type_name: string;
  kind: "new_contract" | "amendment";
  mode: "signed" | "notice";
  status: "awaiting_signature" | "completed" | "declined" | "notified" | "failed";
  sent_at: string | null;
  effective_date: string | null;
  completed_at: string | null;
  counterparty: string | null;
  signing_session_id: number | null;
  waiting_on: "client" | "pa" | null;
  can_sign: boolean;
  parent_document_id: number | null;
};

/** A contract the PA holds with Sidekick itself (employment). */
export type OwnContract = {
  id: number;
  document_type: string | null;
  type_name: string;
  signed_date: string | null;
  has_pdf: boolean;
};

export const CONTRACT_DOCS_KEY = "/contract-documents/pa";
export const OWN_CONTRACTS_KEY = "/contract-documents/pa/own-contracts";

/** Newest first. Errors propagate — see the note on `useLegalDocuments`. */
export function useMyContractDocuments(enabled = true) {
  return useQuery<{ documents: MyContractDoc[] }, Error, MyContractDoc[]>({
    queryKey: [CONTRACT_DOCS_KEY],
    enabled,
    select: (data) =>
      [...(data?.documents ?? [])].sort(
        (a, b) => sentTime(b.sent_at) - sentTime(a.sent_at),
      ),
  });
}

export function useOwnContracts(enabled = true) {
  return useQuery<{ contracts: OwnContract[] }, Error, OwnContract[]>({
    queryKey: [OWN_CONTRACTS_KEY],
    enabled,
    select: (data) => data?.contracts ?? [],
  });
}

function sentTime(iso: string | null): number {
  return iso ? new Date(iso).getTime() : 0;
}

/** Date-only values ("2026-10-20") parse as local, not UTC midnight. */
export function formatDocDate(iso: string | null | undefined): string {
  return iso ? format(parseISO(iso), "MMM d, yyyy") : "—";
}

/** The status in plain words, for the PA rather than for Ops. */
export function contractDocStatus(doc: MyContractDoc): { label: string; kind: ChipKind } {
  switch (doc.status) {
    case "awaiting_signature":
      if (doc.can_sign) return { label: "Waiting for your signature", kind: "amber" };
      if (doc.waiting_on === "client") return { label: "Waiting for the client", kind: "blue" };
      return { label: "Waiting for signature", kind: "grey" };
    case "completed":
      return { label: `Signed ${formatDocDate(doc.completed_at)}`, kind: "green" };
    case "declined":
      return { label: "Declined", kind: "red" };
    case "notified": {
      if (!doc.effective_date) return { label: "Notice", kind: "teal" };
      const effective = parseISO(doc.effective_date);
      return isAfter(effective, startOfDay(new Date()))
        ? { label: `Notice · takes effect ${formatDocDate(doc.effective_date)}`, kind: "teal" }
        : { label: `Notice · in effect since ${formatDocDate(doc.effective_date)}`, kind: "teal" };
    }
    case "failed":
      return { label: "Notice not delivered", kind: "red" };
    default:
      return { label: doc.status, kind: "grey" };
  }
}

/**
 * Fetch a PDF from our API and open it in a new tab. Bytes go through the API
 * (with the bearer token) rather than a presigned link, same as the signing
 * dialog's preview.
 */
export async function openPdf(path: string): Promise<void> {
  const res = await apiRequest("GET", path);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
